<template>
  <!-- Desktop -->
  <div class="hidden sm:flex sm:flex-col sm:h-screen bg-white dark:bg-[#0d1117]">
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5">
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">Tasks</h1>
        <div class="flex items-center gap-3">
          <div class="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button
              v-for="v in VIEWS" :key="v.value"
              @click="view = v.value"
              class="px-3 py-1 text-xs font-semibold rounded-md transition-colors"
              :class="view === v.value ? 'bg-white dark:bg-[#1C2128] text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'"
            >{{ v.label }}</button>
          </div>
          <button
            v-if="view !== 'stats'"
            @click="onNewTask"
            class="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >New task</button>
        </div>
      </div>
    </div>
    <div class="flex-1 overflow-hidden">
      <TasksChecklist v-if="view === 'checklist'" ref="desktopChecklistRef" />
      <TasksBoard v-else-if="view === 'board'" ref="desktopBoardRef" />
      <TasksStats v-else />
    </div>
  </div>

  <!-- Mobile: Material-style top app bar (elevation, not a border; a
       sliding-indicator tab strip, not floating pills) + a Floating Action
       Button for "New" instead of a header button — the header pill/pill-tab
       combo read as a shrunk desktop bar, not an Android surface. -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_6px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_2px_6px_rgba(0,0,0,0.3)]">
      <div class="flex items-center px-4 h-14">
        <h1 class="text-xl font-medium text-gray-900 dark:text-white tracking-tight">Tasks</h1>
      </div>
      <div class="relative flex">
        <button
          v-for="v in VIEWS" :key="v.value"
          @click="view = v.value"
          class="flex-1 py-3 text-[13px] font-semibold tracking-wide uppercase transition-colors active:bg-black/[0.03] dark:active:bg-white/[0.05]"
          :class="view === v.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'"
        >{{ v.label }}</button>
        <span
          class="absolute bottom-0 h-[3px] rounded-t-full bg-blue-600 dark:bg-blue-400 transition-[left] duration-300 ease-out"
          :style="{ width: `${100 / VIEWS.length}%`, left: `${activeIndex * (100 / VIEWS.length)}%` }"
        ></span>
      </div>
    </div>
    <div class="pb-20">
      <TasksChecklist v-if="view === 'checklist'" ref="mobileChecklistRef" variant="mobile" />
      <TasksBoard v-else-if="view === 'board'" ref="mobileBoardRef" variant="mobile" />
      <TasksStats v-else />
    </div>

    <!-- FAB — the Android-native way to expose "create," not a header pill -->
    <button
      v-if="view !== 'stats'"
      @click="onNewTask"
      aria-label="New task"
      class="fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-blue-600 text-white shadow-[0_3px_5px_rgba(0,0,0,0.2),0_7px_12px_rgba(0,0,0,0.15)] active:scale-95 active:shadow-[0_2px_3px_rgba(0,0,0,0.2)] flex items-center justify-center transition-transform duration-150"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="2.25" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
      </svg>
    </button>
  </div>

</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useGlobalStore } from 'stores/global.js'
import { useTasksStore } from 'stores/tasks.js'
import { useTaskStatsStore } from 'stores/taskStats.js'
import { useTasksUrlState } from 'composables/useTasksUrlState.js'
import { TASK_COLORS } from './taskColors.js'
import { PRIORITY_ORDER } from './taskPriorities.js'
import TasksChecklist from './TasksChecklist.vue'
import TasksBoard from './TasksBoard.vue'
import TasksStats from './TasksStats.vue'

const VIEWS = [
  { value: 'checklist', label: 'Checklist' },
  { value: 'board', label: 'Board' },
  { value: 'stats', label: 'Stats' },
]

// window.CURRENT_USER (the initial page-load hydration — see
// ApplicationHelper) never includes date_format/timezone/time_format;
// globalStore.init() only pre-fills them from a localStorage cache
// (cal_user_prefs) written by the *last* successful refreshUser() call.
// That cache can go stale — change your date format in Settings, come
// straight to /tasks without anything re-fetching in between, and every
// due-date label here would keep reading the old preference. Unconditional
// (not "only if missing" — the cache is populated, just possibly wrong)
// refresh on mount so every due date shown on this page reflects the
// account's *current* date_format, not a stale local copy.
const globalStore = useGlobalStore()
onMounted(() => { globalStore.refreshUser() })

// Validated against VIEWS rather than checked for one known string. The
// previous version tested `=== 'board'` and fell back to checklist otherwise,
// which silently made Stats unrestorable the moment it stopped being a
// placeholder: you could leave the page on Stats and always come back to
// Checklist. Deriving the valid set from VIEWS means a fourth tab is
// restorable the day it is added, with nothing here to remember to update.
const VIEW_KEY = 'tasks_view'
const DEFAULT_VIEW = 'checklist'

function storedView() {
  try {
    const stored = localStorage.getItem(VIEW_KEY)
    return VIEWS.some(v => v.value === stored) ? stored : DEFAULT_VIEW
  } catch {
    // Private mode, or site data blocked. A remembered tab is a convenience,
    // never a reason to fail to render the page.
    return DEFAULT_VIEW
  }
}

// ── URL as shareable state ────────────────────────────────────────────────
// This component is the single owner of the query string. The individual
// views deliberately know nothing about the router: both a desktop and a
// mobile instance of every view is mounted at once, so letting them write the
// URL themselves would mean two components racing to describe one page.
const tasksStore = useTasksStore()
const statsStore = useTaskStatsStore()
const { readState, writeState } = useTasksUrlState()

const urlState = readState(TASK_COLORS.map(c => c.value), PRIORITY_ORDER)

// URL beats localStorage: opening a link someone sent should show you their
// view, not yours. Applied here in setup rather than onMounted so the child
// views already see the right state when they mount and run their own fetches.
if (urlState.checklistScope) tasksStore.hydrateChecklistScope(urlState.checklistScope)
if (urlState.statsFilters) statsStore.hydrateFilters(urlState.statsFilters)

const view = ref(urlState.tab || storedView())

function syncUrl({ push = false } = {}) {
  writeState({
    tab: view.value,
    checklistScope: tasksStore.checklistScope,
    statsFilters: statsStore.filters,
  }, { push })
}

// A tab is somewhere you went, so Back should return to the previous one.
watch(view, (v) => {
  try { localStorage.setItem(VIEW_KEY, v) } catch { /* see storedView */ }
  syncUrl({ push: true })
})

// Filters are adjustments to where you already are: replace, so Back does not
// become an undo trail of every checkbox touched.
watch(() => statsStore.filters, () => syncUrl(), { deep: true })
watch(() => tasksStore.checklistScope, () => syncUrl())

// Reflect whatever was restored from localStorage, so the URL is shareable
// immediately rather than only after the first interaction.
onMounted(() => syncUrl())

// Position of the mobile tab strip's sliding underline indicator.
const activeIndex = computed(() => VIEWS.findIndex(v => v.value === view.value))

// Desktop/mobile instances of both views are always mounted (shown/hidden
// purely via responsive CSS, this app's usual pattern — see CLAUDE.md), so
// there are two refs per view. Only one is ever visible at a time;
// triggering startAdd() on both is harmless since each keeps its own local
// add-row state. There's no standalone create page anymore — "New task"
// always opens the relevant view's own inline quick-add (checklist: a row
// at the top of the list; board: the Pending column's "+ Add task" row).
const desktopChecklistRef = ref(null)
const mobileChecklistRef = ref(null)
const desktopBoardRef = ref(null)
const mobileBoardRef = ref(null)

function onNewTask() {
  if (view.value === 'checklist') {
    desktopChecklistRef.value?.startAdd()
    mobileChecklistRef.value?.startAdd()
  } else {
    desktopBoardRef.value?.startAdd('pending')
    mobileBoardRef.value?.startAdd('pending')
  }
}
</script>
